import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { computeDefaultTaxYear } from '@/utils/taxEngine';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

export const LanguageContext = createContext<'en'|'ur'>('en');

export const T = ({ en, ur }: { en: React.ReactNode, ur: React.ReactNode }) => {
  const lang = useContext(LanguageContext);
  return <>{lang === 'ur' ? ur : en}</>;
};

// The FormData interface is updated to include the new 'filing_stage'
export interface FormData {
  id?: string;
  user_id?: string;
  has_fbr_account?: boolean;
  fbr_username?: string;
  fbr_password?: string;
  tax_year?: string;
  cnic?: string;
  full_name?: string;
  address?: string;
  phone?: string;
  email?: string;
  nationality?: 'pakistani' | 'foreigner';
  residential_status?: 'resident' | 'non_resident';
  income_salary?: number;
  income_property?: number;
  income_business?: number;
  income_pension?: number;
  income_agricultural?: number;
  income_profit_debt?: number;
  income_foreign_source?: number;
  calculated_tax?: number;
  tax_class?: string;
  total_income?: number;
  effective_rate?: number;
  additional_tax_deductions?: number;
  tax_deducted_salary?: number;
  tax_deducted_property?: number;
  tax_deducted_business?: number;
  tax_deducted_profit_debt?: number;
  cash_in_hand?: number;
  other_assets?: string;
  liabilities?: string;
  other_details?: string;
  // Wealth Reconciliation fields
  personal_expenses_details?: any;
  personal_expenses?: number;
  previous_year_net_assets?: number;
  current_year_net_assets?: number;
  wealth_increase?: number;
  reconciled_amount?: number;

  bank_details?: any[];
  motor_vehicles?: any[];
  properties?: any[];
  investments?: any[];
  precious_possessions?: any[];
  liabilities_details?: any[];
  wht_mobile_internet?: number;
  wht_vehicle_token?: number;
  wht_other?: number;
  tax_deducted_from?: any;
  status?: string;
  filing_stage?: 'draft' | 'submitted'; // New field
}

interface TaxFormContextType {
  currentStep: number;
  formData: FormData;
  setCurrentStep: (step: number) => void;
  updateFormData: (data: Partial<FormData>, skipAutosave?: boolean) => void;
  nextStep: () => void;
  prevStep: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  totalSteps: number;
  saveDraft: () => Promise<string | null>; // Modified return type
  handleSubmit: () => Promise<{ success: boolean }>;
  isLoading: boolean;
  isSaving: boolean;
  lang: 'en' | 'ur';
  setLang: (lang: 'en' | 'ur') => void;
}

const TaxFormContext = createContext<TaxFormContextType | undefined>(undefined);

export const useTaxForm = () => {
  const context = useContext(TaxFormContext);
  if (!context) {
    throw new Error('useTaxForm must be used within TaxFormProvider');
  }
  return context;
};

// --- MODIFIED: Added props for Admin and strictly separated new tasks ---
export const TaxFormProvider: React.FC<{ children: React.ReactNode; initialData?: FormData; readOnly?: boolean; isNew?: boolean; targetTaxYear?: string }> = ({ children, initialData, readOnly = false, isNew = false, targetTaxYear }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const safeParse = (val: any) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val); 
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) { return []; }
    }
    return [];
  };

  // Initialize with initialData if provided, ensuring JSON arrays are parsed
  const [formData, setFormData] = useState<FormData>(() => {
    if (initialData) {
      return {
        ...initialData,
        bank_details: safeParse(initialData.bank_details),
        motor_vehicles: safeParse(initialData.motor_vehicles),
        properties: safeParse(initialData.properties),
        investments: safeParse(initialData.investments),
        precious_possessions: safeParse(initialData.precious_possessions),
        liabilities_details: safeParse(initialData.liabilities_details),
        personal_expenses_details: typeof initialData.personal_expenses_details === 'string' ? (()=>{try{return JSON.parse(initialData.personal_expenses_details)}catch{return {}}})() : (initialData.personal_expenses_details || {}),
      } as FormData;
    }
    return { tax_year: targetTaxYear || computeDefaultTaxYear() };
  });
  const [isLoading, setIsLoading] = useState(!initialData && !isNew);
  const [isSaving, setIsSaving] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const totalSteps = 6;
  const [activeTaxYear, setActiveTaxYear] = useState<string>(initialData?.tax_year || targetTaxYear || computeDefaultTaxYear());
  const [lang, setLang] = useState<'en'|'ur'>('en');

  // Autosave configuration
  const saveDraftRef = useRef<() => Promise<string | null>>();
  const debounceRef = useRef<NodeJS.Timeout>();

  const updateFormData = (data: Partial<FormData>, skipAutosave = false) => {
    if (readOnly) return; // Prevent updates in read-only mode
    if (data.tax_year && data.tax_year !== activeTaxYear) {
      setActiveTaxYear(data.tax_year);
    }
    
    setFormData(prev => ({ ...prev, ...data }));

    if (!skipAutosave) {
       // Trigger implicit Autosave Sequence!
       if (debounceRef.current) clearTimeout(debounceRef.current);
       debounceRef.current = setTimeout(() => {
          if (saveDraftRef.current) saveDraftRef.current();
       }, 2000);
    }
  };

  const nextStep = () => currentStep < totalSteps && setCurrentStep(currentStep + 1);
  const prevStep = () => currentStep > 1 && setCurrentStep(currentStep - 1);

  // --- MODIFIED: `saveDraft` now respects readOnly mode and returns ID ---
  const saveDraft = useCallback(async (): Promise<string | null> => {
    if (readOnly) return null; // Don't save in read-only mode
    if (isLoading) return null; // Prevent blank autosave while loading existing data
    if (!user && !formData.user_id) return null; // Need a user or a user_id in formData to save
    if (Object.keys(formData).length <= 1 && !formData.id) return null; // Don't save empty form unless it's an existing record

    setIsSaving(true);
    try {
      const { ...saveData } = formData;
      const payload: any = {
        ...saveData,
        user_id: formData.user_id || user?.id, // Use formData.user_id if available, otherwise current user's ID
        tax_year: activeTaxYear,
        full_name: formData.full_name || 'Draft User', // Prevent NOT NULL SQL Crash
        cnic: formData.cnic || '00000-0000000-0',
        email: formData.email || user?.email || 'draft@makemefiler.com',
        filing_stage: 'draft', // Always save as a draft
        // Ensure JSON fields are natively mapped (Supabase Client natively stringifies JSONB, double stringifying breaks arrays)
        bank_details: Array.isArray(formData.bank_details) ? formData.bank_details : [],
        motor_vehicles: Array.isArray(formData.motor_vehicles) ? formData.motor_vehicles : [],
        properties: Array.isArray(formData.properties) ? formData.properties : [],
        investments: Array.isArray(formData.investments) ? formData.investments : [],
        precious_possessions: Array.isArray(formData.precious_possessions) ? formData.precious_possessions : [],
        liabilities_details: Array.isArray(formData.liabilities_details) ? formData.liabilities_details : [],
        tax_deducted_from: Array.isArray(formData.tax_deducted_from) ? formData.tax_deducted_from : [],
        personal_expenses_details: formData.personal_expenses_details || {},
      };

      // Upsert Logic using API
      let targetId = formData.id;
      
      // Removed dangerous targetId probe logic that blindly overwrote DB data with blank frontend states

      let resultId = targetId;

      if (targetId) {
        // UPDATE
        await api.put(`/data/tax_returns/${targetId}`, payload);
      } else {
        // INSERT
        // Generate UUID if backend doesn't (Backend expects ID?)
        // The backend generic INSERT: `INSERT INTO ${tableName} (${keys.join(', ')}) VALUES ...`
        // If ID is auto-incrementint/UUID default, we don't need to send it.
        // Tax returns ID is likely UUID. Let's assume DB generates it or we need to send it.
        // Checking backend: `req.body` keys are used.
        // If I don't send ID, DB should generate.

        // Remove undefined ID from payload if it exists
        if (!payload.id) delete payload.id;

        const res = await api.post('/data/tax_returns', payload);
        if (res && res.id) {
          resultId = res.id;
        } else if (res && res.insertId) { 
          resultId = res.insertId.toString();
        } else {
          // Fallback probe if backend response lacks ID format
          const inserted = await api.get(`/data/tax_returns?user_id=${payload.user_id}&tax_year=${payload.tax_year}`);
          if (inserted && inserted.length > 0) {
            resultId = inserted[0].id;
          }
        }
      }

      // Update the form state with the returned ID without mutating the arrays into strings
      if (resultId) {
        setFormData(prev => ({ ...prev, id: resultId }));
        return resultId;
      }
      return null;

    } catch (error: any) {
      console.error("Draft save failed:", error);
      toast({ 
         title: "Sync Error", 
         description: error.message?.includes('column') ? "Missing database column. Please run the SQL sync script." : (error.message || "Failed to save draft securely."), 
         variant: "destructive" 
      });
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [user, formData, activeTaxYear, toast, readOnly, isLoading]);

  // Keep ref up to date to prevent closure stale state during debounce
  useEffect(() => {
    saveDraftRef.current = saveDraft;
  }, [saveDraft]);

  // --- NEW: `handleSubmit` is now centralized here ---
  const handleSubmit = async (): Promise<{ success: boolean }> => {
    if (readOnly) return { success: false };

    // Let's fix saveDraft to use formData.user_id if available, otherwise user.id
    const targetUserId = formData.user_id || user?.id;

    // We don't check for ID here anymore, we try to save first
    if (!targetUserId) {
      toast({ title: "Submission Failed", description: "User not identified.", variant: "destructive" });
      return { success: false };
    }

    // User specifically requested to allow submitting unreconciled returns without block
    // const equationDiff = Math.abs((formData.wealth_increase || 0) - ((formData.total_income || 0) - (formData.personal_expenses || 0)));
    // if (equationDiff > 10) {
    //    toast({ 
    //      title: "FBR Submission Locked", 
    //      description: `Unreconciled variance of ${equationDiff.toLocaleString()} PKR detected by backend validation.`, 
    //      variant: "destructive" 
    //    });
    //    return { success: false };
    // }

    setIsSaving(true);
    toast({ title: "Submitting...", description: "Please wait while we finalize your tax return." });

    try {
      // Ensure the latest data is saved as a draft first.
      // CRITICAL FIX: Wait for saveDraft to return the ID
      const savedId = await saveDraft();

      // Use the returned ID or fall back to state ID if saveDraft didn't return (e.g. no changes)
      const taxReturnId = savedId || formData.id;

      if (!taxReturnId) {
        throw new Error("Could not save tax return draft. Please try again.");
      }

      // Final step: Update the filing_stage to 'submitted'
      const date = new Date();
      const dateStr = `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}`;
      const randomBits = Math.random().toString(36).substring(2, 6).toUpperCase();
      const tracking_id = `TRK-FBR-${dateStr}-${randomBits}`;
      
      await api.put(`/data/tax_returns/${taxReturnId}`, { 
          filing_stage: 'submitted', 
          status: 'Submitted',
          tracking_id
      });
      localStorage.setItem('filing_tracking_id', tracking_id);

      toast({ title: "✅ Success!", description: "Your tax return has been submitted." });
      setFormData(prev => ({ ...prev, filing_stage: 'submitted' }));
      return { success: true };

    } catch (error: any) {
      console.error("Submission failed:", error);
      toast({ title: "Submission Failed", description: error.message, variant: "destructive" });
      return { success: false };
    } finally {
      setIsSaving(false);
    }
  };

  // --- MODIFIED: `useEffect` for loading data from the unified table ---
  useEffect(() => {
    // If initialData is provided OR we strictly requested a NEW context, we don't fetch from DB.
    if (initialData || isNew) {
      setIsLoading(false);
      return;
    }

    const loadData = async () => {
      if (!user) {
        setFormData({ tax_year: activeTaxYear });
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const existing = await api.get(`/data/tax_returns?user_id=${user.id}&tax_year=${activeTaxYear}`);
        const existingReturn = existing && existing.length > 0 ? existing[0] : null;

        if (existingReturn) {
          // If a submitted return is found, notify the user.
          if (existingReturn.filing_stage === 'submitted') {
            toast({
              title: "Previously Submitted Return Loaded",
              description: `You are viewing a return for ${activeTaxYear} that has already been submitted.`,
              variant: "default",
              duration: 5000,
            });
          } else {
            toast({
              title: "Draft Loaded! ✅",
              description: `Your saved draft for tax year ${activeTaxYear} has been loaded.`,
            });
          }
          // Parse JSON fields if they are strings (MySQL might return them as strings if stored as TEXT/JSON)
          // If my generic API returns JSON columns as objects, good. If strings, need parsing.
          // Safe parse helper now defined at the top

          const cleanData = {
            ...existingReturn,
            bank_details: safeParse(existingReturn.bank_details),
            motor_vehicles: safeParse(existingReturn.motor_vehicles),
            properties: safeParse(existingReturn.properties),
            investments: safeParse(existingReturn.investments),
            precious_possessions: safeParse(existingReturn.precious_possessions),
            liabilities_details: safeParse(existingReturn.liabilities_details),
            tax_deducted_from: safeParse(existingReturn.tax_deducted_from),
            personal_expenses_details: typeof existingReturn.personal_expenses_details === 'string' ? (()=>{try{return JSON.parse(existingReturn.personal_expenses_details)}catch{return {}}})() : (existingReturn.personal_expenses_details || {}),
          };

          setFormData(cleanData as FormData);
        } else {
          setFormData({ tax_year: activeTaxYear });
          toast({
            title: "New Draft Started 📄",
            description: `No record was found for ${activeTaxYear}. Starting a new one.`,
          });
        }
      } catch (error: any) {
        console.error("Failed to load data:", error);
        toast({ title: "Error Loading Data", description: error.message, variant: "destructive" });
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [user, activeTaxYear, toast, initialData]);

  // Autosave functionality remains
  useEffect(() => {
    if (readOnly) return;
    const interval = setInterval(saveDraft, 30000); // Autosave every 30 seconds
    return () => clearInterval(interval);
  }, [saveDraft, readOnly]);

  return (
    <LanguageContext.Provider value={lang}>
      <TaxFormContext.Provider
        value={{
          currentStep, formData, setCurrentStep, updateFormData, nextStep, prevStep,
          isFirstStep: currentStep === 1, isLastStep: currentStep === totalSteps,
          totalSteps, saveDraft, isLoading, isSaving, lang, setLang,
          handleSubmit // Expose the centralized submit function
        }}
      >
        {children}
      </TaxFormContext.Provider>
    </LanguageContext.Provider>
  );
};